import { BottomSheet, Button, SelectItem, SkeletonPrimitive } from '@watersport/ui-react-next'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useLayout } from 'contexts/LayoutProvider'
import ActivityGridItem from 'features/common/components/ActivityGridItem'
import ActivityModalHeader from 'features/common/components/ActivityModalHeader'
import { cn } from 'features/common/helpers/cn'
import { Activity, SearchScope } from 'features/common/types'
import { useGetActivitiesQuery } from 'store/booking/api'

export interface ActivityModalProps {
  show: boolean
  locationId: string | undefined
  scope: SearchScope | undefined
  polygons?: [number, number][][] | undefined
  selected: Activity[]
  onSelect?: (activities: Activity[], selectedAllActivities: boolean) => void
  onClose?: () => void
}

const ActivityModal: React.FC<ActivityModalProps> = ({
  locationId,
  scope,
  polygons,
  show,
  onClose,
  selected,
  onSelect,
}) => {
  const [activities, setActivities] = useState<Activity[]>(selected)
  const [selectedAll, setSelectedAll] = useState<boolean>(true)
  const [isGridView, setIsGridView] = useState(true)
  const { activeRefs } = useLayout()

  const { data, isFetching } = useGetActivitiesQuery(
    {
      locationId,
      lat: scope?.center?.lat,
      lng: scope?.center?.lng,
      radius: scope?.distance,
      polygons: polygons && JSON.stringify(polygons),
    },
    { refetchOnMountOrArgChange: true },
  )

  const allActivities = useMemo(
    () => [...(data?.popular_items || []), ...(data?.extra_items || [])],
    [data?.extra_items, data?.popular_items],
  )

  const onCheckedChange = useCallback(
    (status: boolean) => {
      if (status) {
        setActivities(allActivities)
      } else {
        setActivities([])
      }
      setSelectedAll(status)
    },
    [allActivities],
  )

  const onSelectItem = useCallback(
    (item: Activity) => {
      const checked = activities.find(i => i._id === item._id)
      if (checked) {
        setActivities(activities.filter(i => i._id !== item._id))
      } else {
        setActivities([...activities, item])
      }
    },
    [activities],
  )

  const confirm = useCallback(() => {
    const filteredActivities = activities.filter(activity =>
      allActivities.some(allActivity => allActivity._id === activity._id),
    )
    if (onSelect) onSelect(filteredActivities, selectedAll)
    if (onClose) onClose()
  }, [activities, allActivities, onClose, onSelect, selectedAll])

  const searchText = useMemo(
    () => (activities.length > 0 ? 'Save' : 'Select at least 1 water sport'),
    [activities.length],
  )

  useEffect(() => {
    if (show && allActivities.length > 0) {
      if (selected.length === 0) {
        setActivities(allActivities)
        setSelectedAll(true)
      } else {
        setActivities(selected)
        setSelectedAll(selected.length === allActivities.length)
      }
    }
  }, [show, allActivities, selected])

  useEffect(() => {
    if (data && activities.length === allActivities.length) {
      setSelectedAll(true)
    } else {
      setSelectedAll(false)
    }
  }, [activities.length, allActivities.length, data])

  useEffect(() => {
    if (locationId && !isFetching) {
      const updatedActivities = selected.filter(selectedItem =>
        allActivities.some(activity => activity._id === selectedItem._id),
      )
      setActivities(updatedActivities)
      setSelectedAll(updatedActivities.length === allActivities.length)
    }
  }, [locationId, allActivities, selected, isFetching])

  useEffect(() => {
    if (onSelect && (locationId || scope || polygons)) onSelect(allActivities, true)
  }, [allActivities, onSelect, locationId, scope, polygons])

  return (
    <BottomSheet
      show={show}
      onClose={onClose}
      title={'Select water sports'}
      headerClassName="p-4 pl-5"
      titleClassName="font-medium"
      closeButtonClassName="p-1 border-blue-400 text-blue-200"
      fullHeight
      activeRefs={activeRefs}
    >
      <div className="flex flex-col h-full" data-testid="activity-modal">
        <div className="flex flex-col flex-1 overflow-hidden">
          <ActivityModalHeader
            isGridView={isGridView}
            setIsGridView={setIsGridView}
            selectedAll={selectedAll}
            onCheckedChange={onCheckedChange}
            locationId={locationId}
            totalGroupCount={data?.total_group_count}
          />
          <div className="flex-1 overflow-y-auto no-scrollbar">
            {(data?.popular_items.length || 0) > 0 && (
              <div data-testid="popular-activities-section">
                <div
                  className="flex justify-center items-center px-5 py-[9px] bg-blue-100"
                  data-testid="popular-activities-header"
                >
                  <h1 className="text-sm font-medium text-blue-800 tracking-wider">
                    {'MOST POPULAR'}
                  </h1>
                </div>
                <div
                  className={`flex-1 px-5 ${isGridView ? 'grid grid-cols-2 gap-x-4 gap-y-5 py-5' : 'flex flex-col'}`}
                  data-testid="popular-activities-list"
                >
                  {data?.popular_items.map((item, index) => {
                    const isSelected = activities.some(i => i._id === item._id)
                    const commonProps = {
                      selected: isSelected,
                      onClick: () => onSelectItem(item),
                      item: {
                        ...item,
                        label: item.group_count
                          ? `${item.short_name} (${item.group_count})`
                          : item.short_name,
                        key: item._id,
                      },
                    }

                    return isGridView ? (
                      <ActivityGridItem
                        key={index}
                        {...commonProps}
                        item={{
                          ...commonProps.item,
                          file: item.files,
                        }}
                        dataTestId={`popular-activity-grid-item-${index}`}
                      />
                    ) : (
                      <SelectItem
                        key={index}
                        {...commonProps}
                        dataTestId={`popular-activity-list-item-${index}`}
                        active
                      />
                    )
                  })}
                </div>
              </div>
            )}
            <div
              className="flex justify-center items-center px-5 py-[9px] bg-blue-100"
              data-testid="all-activities-header"
            >
              <h1 className="text-sm font-medium text-blue-800 tracking-wider">
                {'ALL WATER SPORTS'}
              </h1>
            </div>
            <div className="flex-1 px-5" data-testid="all-activities-section">
              {isFetching &&
                [1, 2, 3].map(key => (
                  <div
                    key={key}
                    className="p-3 border-b border-blue-200 space-y-2"
                    data-testid="activities-loading-skeleton"
                  >
                    <SkeletonPrimitive.Skeleton className="h-4 w-[250px] bg-blue-200 rounded-full" />
                  </div>
                ))}
              <div
                className={`flex-1 ${isGridView ? 'grid grid-cols-2 gap-x-4 gap-y-5 py-5' : 'flex flex-col'}`}
              >
                {data?.extra_items.map((item, index) => {
                  const isSelected = activities.some(i => i._id === item._id)
                  const commonProps = {
                    selected: isSelected,
                    onClick: () => onSelectItem(item),
                    item: {
                      ...item,
                      label: item.group_count
                        ? `${item.short_name} (${item.group_count})`
                        : item.short_name,
                      key: item._id,
                    },
                  }

                  return isGridView ? (
                    <ActivityGridItem
                      key={index}
                      {...commonProps}
                      item={{
                        ...commonProps.item,
                        file: item.files,
                      }}
                      dataTestId={`activity-grid-item-${index}`}
                    />
                  ) : (
                    <SelectItem
                      key={index}
                      {...commonProps}
                      dataTestId={`activity-list-item-${index}`}
                      active
                    />
                  )
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="p-5 bg-blue-100">
          <Button
            active={activities.length > 0}
            className={cn('')}
            variant={'default'}
            text={searchText}
            rounded
            size={'xl'}
            onClick={confirm}
            data-testid="save-activities-button"
          />
        </div>
      </div>
    </BottomSheet>
  )
}

export default ActivityModal
